import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Search,
  PlusSquare,
  MessageCircle,
  User,
  Bell,
} from 'lucide-react';
import { SocialFeed, type ViewProfileData } from './SocialFeed';
import { ExplorePage } from './ExplorePage';
import { CreatePost, type NewPostPayload } from './CreatePost';
import { MessagingHub } from './MessagingHub';
import { PetSocialProfile } from './PetSocialProfile';
import { OtherPetProfile } from './OtherPetProfile';
import { NotificationPanel } from './NotificationPanel';
import { PostDetailModal } from './PostDetailModal';
import type { Notification } from './notificationTypes';
import type { PetForSocial, SocialPost } from './socialTypes';
import { resolveTaggedPets } from './socialTypes';
import { PLATFORM_LABELS } from './crossPostConstants';
import { useSocialFeed } from '../../context/SocialFeedContext';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import * as socialApi from '@/api/social';

interface PetSocialProps {
  userType: 'owner' | 'professional';
  userId: string;
  pets?: PetForSocial[];
  /** Open Messages tab and start chat with this user (e.g. from Pet Parks Walking Buddy) */
  initialOpenChat?: string | null;
  /** Open Create Post (e.g. from Pet Parks "Share to Social") */
  initialCreatePost?: boolean;
  /** Park ID/name when creating a post from Pet Parks */
  initialParkContext?: string | null;
}

const petsToForSocial = (pets: PetForSocial[]) =>
  pets.map((p) => ({
    id: p.id,
    name: p.name || 'Unknown',
    species: p.species,
    avatar:
      p.avatar ||
      (p.species === 'dog' ? '🐕' : p.species === 'cat' ? '🐱' : '🐾'),
    imageUrl: p.imageUrl,
    photoKey: p.photoKey,
  }));

export function PetSocial({
  userType,
  userId,
  pets = [],
  initialOpenChat,
  initialCreatePost,
  initialParkContext,
}: PetSocialProps) {
  const { user: clerkUser } = useClerkUser();
  /** Use Clerk id when available so feed/profile ownership and filters match backend ownerId. */
  const effectiveUserId = clerkUser?.id ?? userId;
  const [activeTab, setActiveTab] = useState<
    'feed' | 'explore' | 'create' | 'messages' | 'profile'
  >('feed');
  const [viewingProfile, setViewingProfile] = useState<{
    petName: string;
    ownerName: string;
    petAvatar: string;
    ownerAvatar?: string;
    professionalBadge?: string;
    ownerId?: string;
    petId?: string;
    trendingScore?: number;
  } | null>(null);
  const [openChatWith, setOpenChatWith] = useState<{
    id: string;
    name: string;
    avatar?: string | null;
  } | null>(null);
  const [exploreCategory, setExploreCategory] = useState<
    'trending' | 'nearby' | 'professionals' | 'challenges'
  >('trending');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationError, setNotificationError] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [profilePetId, setProfilePetId] = useState<string | null>(null);
  const [challengeCreateTag, setChallengeCreateTag] = useState<string | null>(
    null
  );
  const [userFollowers, setUserFollowers] = useState<number | undefined>(
    undefined
  );
  const [userFollowing, setUserFollowing] = useState<number | undefined>(
    undefined
  );
  /** When set, Stories will add this media as the user's story (e.g. from "Also share to Your Story"). */
  const [addToStoryMedia, setAddToStoryMedia] = useState<{
    url: string;
    isVideo: boolean;
    sharedPlatforms?: import('./socialTypes').SharedPlatform[];
  } | null>(null);
  const [selectedNotificationPost, setSelectedNotificationPost] =
    useState<SocialPost | null>(null);
  /** Fetched when Profile tab is open so we get "my posts" by ownerId even if main feed uses different ids */
  const [profilePosts, setProfilePosts] = useState<SocialPost[]>([]);
  const postsRef = useRef<SocialPost[]>([]);
  const { t } = useTranslation();
  const {
    posts,
    setPosts,
    createPostAndAdd,
    likePost,
    unlikePost,
    addComment,
    isLoading: feedLoading,
  } = useSocialFeed();
  const petsForSocial = useMemo(() => petsToForSocial(pets), [pets]);

  // Fetch "my posts" by ownerId and merge into feed so ownerId/ownerAvatar are correct in feed and profile
  const mergeOwnerPostsIntoFeed = useCallback(() => {
    if (!effectiveUserId) return;
    socialApi
      .getFeed({ ownerId: effectiveUserId, limit: 50 })
      .then(({ posts: fetched }) => {
        const raw = Array.isArray(fetched) ? fetched : [];
        // Normalize ownerId so ownership comparisons work regardless of backend ID format
        const list = raw.map((p) =>
          p.ownerId === effectiveUserId ? p : { ...p, ownerId: effectiveUserId }
        );
        setPosts((prev) => {
          const byId = new Map(prev.map((p) => [p.id, p]));
          list.forEach((p) => byId.set(p.id, p));
          return Array.from(byId.values());
        });
      })
      .catch(() => {});
  }, [effectiveUserId, setPosts]);

  // After feed loads or refetches, re-merge owner posts so avatar/owner persist on refresh
  useEffect(() => {
    if (!feedLoading && effectiveUserId) mergeOwnerPostsIntoFeed();
  }, [feedLoading, effectiveUserId, mergeOwnerPostsIntoFeed]);

  // When user opens Profile tab, fetch posts by ownerId so profile shows their posts
  useEffect(() => {
    if (activeTab !== 'profile' || !effectiveUserId) return;
    let cancelled = false;
    socialApi
      .getFeed({ ownerId: effectiveUserId, limit: 50 })
      .then(({ posts: fetched }) => {
        if (cancelled) return;
        const raw = Array.isArray(fetched) ? fetched : [];
        // Normalize ownerId: posts returned by this query belong to effectiveUserId,
        // but the backend may return a different internal user ID. Stamping here ensures
        // the p.ownerId === userId filter in PetSocialProfile always matches.
        const list = raw.map((p) =>
          p.ownerId === effectiveUserId ? p : { ...p, ownerId: effectiveUserId }
        );
        setProfilePosts(list);
        if (list.length === 0) {
          const fromFeed = postsRef.current.filter(
            (p) => p.ownerId === effectiveUserId
          );
          if (fromFeed.length > 0) setProfilePosts(fromFeed);
        }
        setPosts((prev) => {
          const byId = new Map(prev.map((p) => [p.id, p]));
          list.forEach((p) => byId.set(p.id, p));
          return Array.from(byId.values());
        });
      })
      .catch(() => {
        const fromFeed = postsRef.current.filter(
          (p) => p.ownerId === effectiveUserId
        );
        setProfilePosts(fromFeed);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, effectiveUserId, setPosts]);

  postsRef.current = posts;

  const refreshFollowerCounts = useCallback(() => {
    if (!effectiveUserId) return;
    Promise.all([socialApi.getFollowers(), socialApi.getFollowing()])
      .then(([fr, fg]) => {
        setUserFollowers(fr.total);
        setUserFollowing(fg.total);
      })
      .catch(() => {});
  }, [effectiveUserId]);

  useEffect(() => {
    if (!effectiveUserId) return;
    let cancelled = false;
    Promise.all([socialApi.getFollowers(), socialApi.getFollowing()])
      .then(([fr, fg]) => {
        if (!cancelled) {
          setUserFollowers(fr.total ?? fr.followers?.length ?? 0);
          setUserFollowing(fg.total ?? fg.following?.length ?? 0);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [effectiveUserId]);

  // Refresh follower/following counts when user opens profile tab so stats are up to date
  useEffect(() => {
    if (activeTab === 'profile') refreshFollowerCounts();
  }, [activeTab, refreshFollowerCounts]);

  const handlePostCreated = useCallback(
    async (payload: NewPostPayload) => {
      const taggedPets = resolveTaggedPets(payload.taggedPetIds, petsForSocial);
      const currentUser = effectiveUserId
        ? {
            ownerId: effectiveUserId,
            ownerName: payload.ownerName,
            ownerAvatar: payload.ownerAvatar,
          }
        : undefined;
      const newPost = await createPostAndAdd(
        {
          caption: payload.caption,
          images: payload.images,
          taggedPetIds: payload.taggedPetIds,
          taggedPetSummaries: taggedPets,
          location: payload.location,
          fromPetParks: payload.fromPetParks,
          visibility: payload.visibility,
          sharedPlatforms: payload.sharedPlatforms,
          ownerName: payload.ownerName,
          ownerAvatar: payload.ownerAvatar,
        },
        currentUser
      );
      if (newPost) {
        if (payload.alsoShareToStory && newPost.images?.[0]) {
          setAddToStoryMedia({
            url: newPost.images[0],
            isVideo: !!payload.isVideo,
            sharedPlatforms: payload.sharedPlatforms,
          });
        }
        setActiveTab('feed');
        toast.success(t('social.postShared'));
        if (payload.sharedPlatforms?.length) {
          const names = payload.sharedPlatforms
            .map((p) => PLATFORM_LABELS[p] ?? p)
            .join(', ');
          toast.info(t('social.alsoSharedToPlatforms', { names }));
        }
      } else {
        toast.error(t('social.failedSharePost'));
      }
    },
    [createPostAndAdd, petsForSocial, t]
  );

  const handleViewPetProfile = useCallback((petId: string, name?: string, avatar?: string, trendingScore?: number) => {
    // Check if this is one of the user's own pets
    const isOwnPet = pets.some((p) => p.id === petId);
    if (isOwnPet) {
      setProfilePetId(petId);
      setActiveTab('profile');
    } else {
      // Navigate to OtherPetProfile for other people's pets
      setViewingProfile({
        petName: name ?? 'Pet',
        ownerName: name ?? 'Pet',
        petAvatar: avatar ?? '🐾',
        petId,
        trendingScore,
      });
      setActiveTab('feed');
    }
  }, [pets]);

  const handleViewProfile = useCallback(
    (profileData: ViewProfileData) => {
      if (profileData.ownerId && profileData.ownerId === effectiveUserId) {
        setViewingProfile(null);
        setActiveTab('profile');
        return;
      }
      setViewingProfile({
        petName: profileData.ownerName ?? profileData.petName ?? '',
        ownerName: profileData.ownerName ?? profileData.petName ?? '',
        petAvatar: profileData.petAvatar ?? '🐾',
        ownerAvatar: profileData.ownerAvatar,
        ownerId: profileData.ownerId,
        petId: profileData.petId,
        professionalBadge: profileData.professionalBadge,
      });
      setActiveTab('feed');
    },
    [effectiveUserId]
  );

  const handleViewUserProfile = useCallback(
    (id: string, name: string, avatar?: string | null, type?: 'USER' | 'PET') => {
      if (id === effectiveUserId) {
        setViewingProfile(null);
        setActiveTab('profile');
        return;
      }
      if (type === 'PET') {
        // Viewing a pet profile — set petId so OtherPetProfile fetches tagged posts & pet followers
        setViewingProfile({
          petName: name,
          ownerName: name,
          petAvatar: avatar ?? '🐾',
          ownerAvatar: avatar ?? undefined,
          petId: id,
        });
      } else {
        // Viewing a user profile
        setViewingProfile({
          petName: name,
          ownerName: name,
          petAvatar: avatar ?? '🐾',
          ownerAvatar: avatar ?? undefined,
          ownerId: id,
        });
      }
      setActiveTab('feed');
    },
    [effectiveUserId]
  );

  const unreadCount =
    notificationUnreadCount ?? notifications.filter((n) => !n.isRead).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const result = await socialApi.getNotifications({ limit: 50 });
      const list = Array.isArray(result?.notifications)
        ? result.notifications
        : [];
      const count =
        typeof result?.unreadCount === 'number' ? result.unreadCount : 0;
      setNotificationUnreadCount(count);
      const mapped: Notification[] = list.map((item) => {
        const name = item.triggeredByName ?? '';
        const message =
          item.type === 'like'
            ? t('social.notification.likeMessage', { name })
            : item.type === 'comment'
              ? t('social.notification.commentMessage', { name })
              : item.type === 'follow' || item.type === 'follow_request'
                ? t('social.notification.followMessage', { name })
                : item.type === 'follow_accepted'
                  ? t('social.notification.followAcceptedMessage', { name })
                  : t('social.notification.reactedMessage', { name });
        const entityType: Notification['entityType'] =
          item.type === 'like' || item.type === 'comment' ? 'post' : 'profile';
        return {
          id: item.id ?? '',
          recipientUserId: '',
          actorUserId: item.triggeredBy ?? '',
          actorName: name,
          actorAvatar: item.triggeredByAvatar ?? '🐾',
          type:
            item.type === 'follow' ||
            item.type === 'follow_request' ||
            item.type === 'follow_accepted'
              ? 'friend_request'
              : item.type,
          entityId: item.referenceId ?? null,
          entityType,
          message,
          isRead: Boolean(item.read),
          createdAt: item.createdAt ?? new Date().toISOString(),
        };
      });
      setNotifications(mapped);
      setNotificationError(false);
    } catch {
      setNotifications([]);
      setNotificationUnreadCount(0);
      setNotificationError(true);
    }
  }, [t]);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30s so follow/like/comment notifications appear without a manual panel open
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (notificationOpen) fetchNotifications();
  }, [notificationOpen, fetchNotifications]);

  // Keep notification-opened post in sync with canonical feed data.
  useEffect(() => {
    if (!selectedNotificationPost) return;
    const updated = posts.find((p) => p.id === selectedNotificationPost.id);
    if (updated && updated !== selectedNotificationPost) {
      setSelectedNotificationPost(updated);
    }
  }, [posts, selectedNotificationPost]);

  const handleMarkNotificationRead = (id: string) => {
    socialApi.markNotificationRead(id).then(
      () => {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
        setNotificationUnreadCount((c) => Math.max(0, c - 1));
      },
      () => {}
    );
  };

  const handleMarkAllNotificationsRead = () => {
    socialApi.markAllNotificationsRead().then(
      () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setNotificationUnreadCount(0);
      },
      () => {}
    );
  };

  const handleNotificationClick = async (n: Notification) => {
    if (n.entityType === 'post' || n.entityType === 'photo') {
      setViewingProfile(null);
      setActiveTab('feed');
      if (!n.entityId) {
        toast.info(t('social.openingInFeed'));
        return;
      }
      const existing = posts.find((p) => p.id === n.entityId);
      if (existing) {
        setSelectedNotificationPost(existing);
        return;
      }
      try {
        const { post } = await socialApi.getPostById(n.entityId);
        setPosts((prev) => {
          const idx = prev.findIndex((p) => p.id === post.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = post;
            return next;
          }
          return [post, ...prev];
        });
        setSelectedNotificationPost(post);
      } catch {
        toast.info(t('social.openingInFeed'));
      }
    } else if (n.entityType === 'profile') {
      if (n.actorUserId === effectiveUserId) {
        setViewingProfile(null);
        setActiveTab('profile');
      } else {
        setViewingProfile({
          petName: n.actorName,
          ownerName: n.actorName,
          petAvatar: n.actorAvatar ?? '🐾',
          ownerAvatar: n.actorAvatar ?? undefined,
          ownerId: n.actorUserId,
        });
        setActiveTab('feed');
      }
    } else {
      setActiveTab('feed');
      toast.info(t('social.openingInFeed'));
    }
  };

  const handleNotificationRetry = () => {
    setNotificationError(false);
    fetchNotifications();
  };

  // Deep link: open Messages tab with a specific user (e.g. from Pet Parks Walking Buddy)
  useEffect(() => {
    if (initialOpenChat) {
      setOpenChatWith({ id: '', name: initialOpenChat });
      setActiveTab('messages');
    }
  }, [initialOpenChat]);

  // Deep link: open Create Post (e.g. from Pet Parks "Share to Social")
  useEffect(() => {
    if (initialCreatePost) {
      setActiveTab('create');
    }
  }, [initialCreatePost]);

  const handleOpenChat = (
    userId: string,
    userName: string,
    avatar?: string | null
  ) => {
    setViewingProfile(null);
    setOpenChatWith({ id: userId, name: userName, avatar });
    setActiveTab('messages');
  };

  const handleBackToFeed = () => {
    setViewingProfile(null);
    setActiveTab('feed');
  };

  const handleFilterClick = (
    category: 'trending' | 'nearby' | 'professionals' | 'challenges'
  ) => {
    setExploreCategory(category);
    setActiveTab('explore');
  };

  return (
    <div className="-m-8 bg-[#F7F6FA] dark:bg-[#0B0A12]">
      <div className="min-h-screen">
        {/* Top Navigation Bar */}
        <div className="bg-white dark:bg-[#16151D] border-b border-[#E2DFEA] dark:border-white/8 sticky top-0 z-40 min-w-0 overflow-hidden">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 min-w-0">
            <div className="flex items-center justify-between h-14 sm:h-16 min-w-0">
              {/* Left spacer for centering */}
              <div className="flex-1 min-w-0" />

              {/* Centered nav icons - larger touch targets on mobile */}
              <div className="flex items-center justify-center gap-1 sm:gap-4 shrink-0 min-w-0">
                <button
                  onClick={() => {
                    setViewingProfile(null);
                    setActiveTab('feed');
                  }}
                  className={`flex items-center justify-center gap-2 p-2.5 min-w-10 min-h-10 sm:min-w-0 sm:min-h-0 sm:p-2 transition-colors rounded-lg touch-manipulation ${
                    activeTab === 'feed'
                      ? 'text-[#936FEF]'
                      : 'text-[#464452] dark:text-white/70 hover:text-[#2D2F7E] dark:hover:text-white'
                  }`}
                >
                  <Home
                    className={`w-6 h-6 ${activeTab === 'feed' ? 'fill-[#936FEF]' : ''}`}
                  />
                </button>
                <button
                  onClick={() => {
                    setViewingProfile(null);
                    setActiveTab('explore');
                  }}
                  className={`flex items-center justify-center gap-2 p-2.5 min-w-10 min-h-10 sm:min-w-0 sm:min-h-0 sm:p-2 transition-colors rounded-lg touch-manipulation ${
                    activeTab === 'explore'
                      ? 'text-[#936FEF]'
                      : 'text-[#464452] dark:text-white/70 hover:text-[#2D2F7E] dark:hover:text-white'
                  }`}
                >
                  <Search className="w-6 h-6" />
                </button>
                <button
                  onClick={() => {
                    setViewingProfile(null);
                    setChallengeCreateTag(null);
                    setActiveTab('create');
                  }}
                  className={`flex items-center justify-center gap-2 p-2.5 min-w-10 min-h-10 sm:min-w-0 sm:min-h-0 sm:p-2 transition-colors rounded-lg touch-manipulation ${
                    activeTab === 'create'
                      ? 'text-[#936FEF]'
                      : 'text-[#464452] dark:text-white/70 hover:text-[#2D2F7E] dark:hover:text-white'
                  }`}
                >
                  <PlusSquare className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewingProfile(null);
                    setActiveTab('messages');
                  }}
                  className={`flex items-center justify-center gap-2 p-2.5 min-w-10 min-h-10 sm:min-w-0 sm:min-h-0 sm:p-2 transition-colors rounded-lg relative touch-manipulation ${
                    activeTab === 'messages'
                      ? 'text-[#936FEF]'
                      : 'text-[#464452] dark:text-white/70 hover:text-[#2D2F7E] dark:hover:text-white'
                  }`}
                  aria-label={t('social.messages.title', 'Messages')}
                >
                  <MessageCircle className="w-6 h-6" />
                  <span
                    className="absolute top-1 right-1 w-2 h-2 bg-[#936FEF] rounded-full pointer-events-none"
                    aria-hidden
                  />
                </button>
                <NotificationPanel
                  notifications={notifications}
                  unreadCount={unreadCount}
                  error={notificationError}
                  onRetry={handleNotificationRetry}
                  onMarkAsRead={handleMarkNotificationRead}
                  onMarkAllRead={handleMarkAllNotificationsRead}
                  onNotificationClick={handleNotificationClick}
                  open={notificationOpen}
                  onOpenChange={setNotificationOpen}
                >
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 p-2.5 min-w-10 min-h-10 sm:min-w-0 sm:min-h-0 sm:p-2 transition-colors rounded-lg relative text-[#464452] dark:text-white/70 hover:text-[#2D2F7E] dark:hover:text-white touch-manipulation"
                    aria-label={t('social.notificationsPanel.title')}
                  >
                    <Bell className="w-6 h-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                </NotificationPanel>
                <button
                  onClick={() => {
                    setViewingProfile(null);
                    setActiveTab('profile');
                  }}
                  className={`flex items-center justify-center gap-2 p-2.5 min-w-10 min-h-10 sm:min-w-0 sm:min-h-0 sm:p-2 transition-colors rounded-lg touch-manipulation ${
                    activeTab === 'profile'
                      ? 'text-[#936FEF]'
                      : 'text-[#464452] dark:text-white/70 hover:text-[#2D2F7E] dark:hover:text-white'
                  }`}
                >
                  <User className="w-6 h-6" />
                </button>
              </div>

              {/* Right: empty flex spacer; language & theme are in dashboard layout to avoid duplication */}
              <div className="flex-1 flex items-center justify-end min-w-0" />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-6xl mx-auto min-w-0 px-0">
          {viewingProfile ? (
            <OtherPetProfile
              profileData={viewingProfile}
              onBack={handleBackToFeed}
              onMessage={handleOpenChat}
              onFollowChange={refreshFollowerCounts}
            />
          ) : (
            <>
              {activeTab === 'feed' && (
                <SocialFeed
                  userType={userType}
                  userId={effectiveUserId}
                  ownerName={
                    clerkUser?.fullName ??
                    clerkUser?.firstName ??
                    (pets[0]?.owner as { name?: string } | undefined)?.name ??
                    t('social.you')
                  }
                  ownerAvatar={clerkUser?.imageUrl}
                  pets={pets}
                  petsForSocial={petsForSocial}
                  posts={posts}
                  onPostsChange={setPosts}
                  onViewProfile={handleViewProfile}
                  onViewPetProfile={handleViewPetProfile}
                  onOpenChat={handleOpenChat}
                  onFilterClick={handleFilterClick}
                  addToStoryMedia={addToStoryMedia}
                  onStoryAdded={() => {
                    // Clear after next tick so Stories has applied the media
                    setTimeout(() => setAddToStoryMedia(null), 0);
                  }}
                />
              )}

              {activeTab === 'explore' && (
                <ExplorePage
                  userType={userType}
                  userId={effectiveUserId}
                  category={exploreCategory}
                  onFilterClick={handleFilterClick}
                  onJoinChallenge={(tag) => {
                    setChallengeCreateTag(tag);
                    setActiveTab('create');
                  }}
                  onBackToFeed={() => setActiveTab('feed')}
                  pets={petsForSocial}
                  onViewPetProfile={handleViewPetProfile}
                />
              )}

              {activeTab === 'create' && (
                <CreatePost
                  userType={userType}
                  userId={effectiveUserId}
                  ownerName={
                    clerkUser?.fullName ??
                    clerkUser?.firstName ??
                    (pets[0]?.owner as { name?: string } | undefined)?.name ??
                    t('social.you')
                  }
                  ownerAvatar={clerkUser?.imageUrl}
                  pets={pets}
                  onClose={() => {
                    setChallengeCreateTag(null);
                    setActiveTab('feed');
                  }}
                  onPostCreated={handlePostCreated}
                  initialLocation={initialParkContext ?? undefined}
                  initialCaption={
                    challengeCreateTag ? `#${challengeCreateTag} ` : undefined
                  }
                />
              )}

              {activeTab === 'messages' && (
                <MessagingHub
                  userType={userType}
                  userId={effectiveUserId}
                  initialChatWith={openChatWith}
                />
              )}

              {activeTab === 'profile' && (
                <PetSocialProfile
                  userType={userType}
                  userId={effectiveUserId}
                  userDisplayName={
                    clerkUser?.fullName ?? clerkUser?.firstName ?? undefined
                  }
                  userAvatarUrl={clerkUser?.imageUrl}
                  pets={pets}
                  posts={(() => {
                    // Always derive from live context posts so mutations (like/delete/comment)
                    // reflect instantly without requiring a page refresh.
                    const fromContext = posts.filter(
                      (p) => p.ownerId === effectiveUserId
                    );
                    return fromContext.length > 0 ? fromContext : profilePosts;
                  })()}
                  initialSelectedPetId={profilePetId}
                  onInitialPetConsumed={() => setProfilePetId(null)}
                  onPostsChange={setPosts}
                  userFollowers={userFollowers}
                  userFollowing={userFollowing}
                  onViewUserProfile={handleViewUserProfile}
                />
              )}
            </>
          )}
        </div>
      </div>
      {selectedNotificationPost && (
        <PostDetailModal
          post={selectedNotificationPost}
          onClose={() => setSelectedNotificationPost(null)}
          onLike={(postId) => {
            const post = posts.find((p) => p.id === postId);
            if (!post) return;
            if (post.isLiked) unlikePost(postId);
            else likePost(postId);
          }}
          onAddComment={(postId, text) => {
            addComment(postId, text);
          }}
        />
      )}
    </div>
  );
}
